import PageHeader from "@/components/PageHeader";
import Spinner from "@/components/Spinner";
import { useToast } from "@/components/ui/use-toast";
import { Module, PATH_ROOT, PATH_SURVEYS } from "@/constants";
import { ApiError } from "@/services/api";
import { fetchSurvey, updateSurvey } from "@/services/survey";
import { ReadSurvey, WriteSurvey } from "@/types/survey";
import { useMutation, useQuery } from "@tanstack/react-query";
import { exists } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SurveyForm from "./SurveyForm";
import AddQuestionButton from "./components/Toolbar/components/AddQuestionButton";
import AddQuestionsFromTemplateButton from "./components/Toolbar/components/AddQuestionsFromTemplateButton";
import ArchiveButton from "./components/Toolbar/components/ArchiveButton";
import ClearButton from "./components/Toolbar/components/ClearButton";
import SaveButton from "./components/Toolbar/components/SaveButton";
import { NotepadText } from "lucide-react";

const SurveyEditor = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const params = useParams();

  const surveyId = params.id as string;

  const querySurvey = () => fetchSurvey(params.id);

  const surveyQuery = useQuery({
    queryKey: [Module.Surveys, params.id],
    queryFn: querySurvey,
  });

  const mutateSurvey = async (values: WriteSurvey) => {
    const response = await updateSurvey(surveyId, values);
    return response;
  };

  const surveyMutation = useMutation<ReadSurvey, ApiError, WriteSurvey>({
    mutationFn: mutateSurvey,
    onSuccess: () => {
      toast({
        title: t("surveyEditor.success.updateSurvey"),
      });
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: t("surveyEditor.errors.updateSurvey"),
        description,
        variant: "destructive",
      });
    },
  });

  const breadcrumbItems = useMemo(
    () => [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("breadcrumb.surveys"), href: PATH_SURVEYS },
      {
        label: t("breadcrumb.survey", {
          name: surveyQuery.data?.name,
        }),
      },
    ],
    [t, surveyQuery.data?.name],
  );

  if (surveyQuery.isLoading) {
    return (
      <Spinner
        label={t("surveyEditor.loadingSurvey")}
        className="w-full h-full "
      />
    );
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <SurveyForm
        isNew={false}
        values={surveyQuery.data}
        busy={surveyMutation.isPending}
        onSubmit={surveyMutation.mutateAsync}
        icon={<NotepadText className="w-6 h-6 mr-3" />}
        title={t("surveyEditor.surveyTitle")}
        buttons={
          <>
            <SaveButton />
            <AddQuestionButton />
            <AddQuestionsFromTemplateButton />
            <ClearButton />
            <ArchiveButton />
          </>
        }
        interQuestionToolbar={(questionIndex) => (
          <>
            <AddQuestionButton
              insertAt={questionIndex}
              label={t("surveyEditor.toolbar.insertQuestion")}
            />
            <AddQuestionsFromTemplateButton
              insertAt={questionIndex}
              label={t("surveyEditor.toolbar.insertQuestionsFromTemplate")}
            />
          </>
        )}
      />
    </>
  );
};

export default SurveyEditor;
